import Vue from 'vue'
import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'
import { whenAuthBootstrapReady } from '@/services/authBootstrap'
import VStripeElements from 'v-stripe-elements'
import { environment, app } from '@/firebase/init'
import { startPermissionsListener } from '@/composables/usePermissions'
import { startEmployeeListener } from '@/composables/useEmployee'
import { createVPhoneInput } from 'v-phone-input'; // https://github.com/paul-thebaud/v-phone-input/tree/2.x.x
import 'v-phone-input/dist/v-phone-input.css';
import 'flag-icons/css/flag-icons.min.css';
import money from './directives/money';
import { initializeFirestore, getFirestore, memoryLocalCache, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore'
import { emitPrerenderReady, isPrerenderBuild } from '@/utils/prerender'
import { resizedUrl } from '@/utils/imageProxy'
import { term } from '@/services/glossary'

// Global template helper: $img(url, width) → CDN-resized WebP via the image
// proxy (or the original URL when the proxy is disabled/SVG/external). Lets any
// component optimize an image with `:src="$img(url, 800)"`.
Vue.prototype.$img = (url, width = 800, opts) => resizedUrl(url, width, opts || {})

// Global template helper: $term('experience') → the operator's word for a concept
// ('Tour Template'), from Leadership → Policies → Glossary. See services/glossary.js.
Vue.prototype.$term = term

const vPhoneInput = createVPhoneInput({
  countryIconMode: 'svg',
});

Vue.use(vPhoneInput);

Vue.config.productionTip = false
// emoji-picker-element is a web component used by the group chat composer.
Vue.config.ignoredElements = [/^emoji-picker$/]

Vue.use(vuetify, {
  iconfont: 'mdi' || 'fa'
})

Vue.use(money, { precision: 2 });
Vue.use(VStripeElements);

Vue.directive('money', money);

// Enable Firestore offline persistence using the current API (best-effort).
// Local development deliberately uses an in-memory cache: a stale local mutation
// in IndexedDB overlays even a fresh server response and can make a live employee
// document appear deleted. Deployed environments retain persistent multi-tab
// caching; prerender builds do not need IndexedDB persistence at all.
if (!isPrerenderBuild()) {
  try {
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
    initializeFirestore(app, {
      localCache: isLocalhost
        ? memoryLocalCache()
        : persistentLocalCache({
            tabManager: persistentMultipleTabManager()
          })
    })
    console.log(
      isLocalhost
        ? '[main] initializeFirestore: memoryLocalCache ready for localhost'
        : '[main] initializeFirestore: persistentLocalCache + persistentMultipleTabManager ready'
    )
  } catch (e) {
    console.warn('[main] initializeFirestore threw, falling back to default Firestore', e)
    try {
      getFirestore(app)
      console.log('[main] fell back to default getFirestore(app) (no persistence)')
    } catch (inner) {
      console.warn('[main] Firestore not initialized for persistence:', e || inner)
    }
  }
} else {
  getFirestore(app)
}

// Permissions listener starts after auth bootstrap (see mount below).

Vue.mixin({
  methods: {
    go: (target, params) => {
      // Support: route object
      if (target && typeof target === 'object') {
        const resolved = router.resolve(target)
        if (resolved.resolved.matched.length > 0) {
          if (router.currentRoute.fullPath !== resolved.route.fullPath) {
            return router.push(target).catch(error => {
              if (error.name != 'NavigationDuplicated' && error.name != 'Error') {
                console.log('ERROR', error)
              }
            })
          }
          return
        }
        return router.push({ name: 'Lost' })
      }

      // Support: raw path string (for CMS pages/articles like '/privacy-policy')
      if (typeof target === 'string' && target.startsWith('/')) {
        const resolved = router.resolve({ path: target })
        if (resolved.resolved.matched.length > 0) {
          if (router.currentRoute.path !== target) {
            return router.push({ path: target }).catch(error => {
              if (error.name != 'NavigationDuplicated' && error.name != 'Error') {
                console.log('ERROR', error)
              }
            })
          }
          return
        }
        return router.push({ name: 'Lost' })
      }

      // Support: external URLs
      if (typeof target === 'string' && /^(https?:\/\/|\/\/|mailto:|tel:)/i.test(target)) {
        // Headless prerender must stay on localhost — navigating away hangs page.goto().
        if (isPrerenderBuild()) {
          emitPrerenderReady()
          return
        }
        // Use a transient <a> click — window.open(..., 'noopener,noreferrer') returns
        // null in modern browsers even when the tab opens, which was triggering the
        // old location.href fallback and navigating the current page too.
        const link = document.createElement('a')
        link.href = target
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }

      // Default: named route string
      const name = target
      const resolved = router.resolve({ name, params })
      if (resolved.resolved.matched.length > 0) {
        const sameName = router.currentRoute.name === name
        const sameParams = JSON.stringify(router.currentRoute.params || {}) === JSON.stringify(params || {})
        if (!sameName || !sameParams) {
          return router.push({ name, params }).catch(error => {
            if (error.name != 'NavigationDuplicated' && error.name != 'Error') {
              console.log('ERROR', error)
            }
          })
        }
        return
      }
      return router.push({ name: 'Lost' })
    },
    contact: function(email) {
      // Support array or comma-separated string
      const emails = Array.isArray(email) 
        ? email.join(',') 
        : (typeof email === 'string' ? email : '');
      
      if (!emails) return;
      
      // Access router from the component context (this.$router)
      const currentRoute = this.$route;
      const routerInstance = this.$router;
      
      // If already on Inbox page, trigger compose via event bus
      if (currentRoute && currentRoute.name === 'AdminCustomerServiceInbox') {
        // Emit event on root instance
        this.$root.$emit('openCompose', emails);
        return;
      }
      
      // Navigate to Inbox with query param
      if (routerInstance) {
        routerInstance.push({
          name: 'AdminCustomerServiceInbox',
          query: { to: emails }
        }).catch(error => {
          if (error.name != 'NavigationDuplicated' && error.name != 'Error') {
            console.log('ERROR', error)
          }
        });
      }
    },
    production: () => {
      return environment === 'production' ? true : false
    },
    development: () => {
      return environment === 'development' ? true : false
    },
  }
})

Vue.config.errorHandler = function (err, vm, info) {
  console.error(err);
}

let appInstance = null

function mountApp() {
  if (appInstance) return
  appInstance = new Vue({
    router,
    vuetify,
    render: h => h(App),
  }).$mount('#app')
}

if (isPrerenderBuild()) {
  mountApp()
} else {
  // Mount immediately so public pages (which need no auth) paint instantly and
  // are never gated behind the auth bootstrap network round-trip. Auth + contact
  // claim + Firestore token fence resolve in the background; the App.vue spinner
  // and the router guard still gate private/admin routes until they're ready.
  mountApp()
  whenAuthBootstrapReady()
    .then(() => {
      startPermissionsListener()
      startEmployeeListener()
    })
    .catch((err) => {
      console.error('[main] whenAuthBootstrapReady failed:', err)
      try {
        startPermissionsListener()
        startEmployeeListener()
      } catch (e) {
        console.error('[main] startPermissionsListener/startEmployeeListener failed:', e)
      }
    })
}